import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getProducts, getspecificVarientsByProduct } from "../../store/AsyncMethods/ProductMethod";
import { getVendors } from "../../store/AsyncMethods/VendorMethod";
import backendVerifiedUser from "../../api/backendVerifiedUser";
import DashboardTiles from "../Dashboard/DashboardTiles";
import ReportsDateFilter from "../Reports/ReportsDateFilter";
import { computeDateRange } from "../Reports/reportFilters";
import { TbAdjustmentsAlt, TbTrendingUp, TbTrendingDown } from "react-icons/tb";
import { MdOutlineReportProblem } from "react-icons/md";

const REASON_OPTIONS = [
  { name: "Damage / Loss", value: "damage" },
  { name: "Wrong Purchase Entry Fix", value: "wrong_purchase" },
  { name: "Stock Found / Recount", value: "recount" },
  { name: "Return to Vendor", value: "return_to_vendor" },
];

const REASON_LABELS = {
  damage: "Damage / Loss",
  wrong_purchase: "Wrong Purchase Fix",
  recount: "Recount",
  return_to_vendor: "Return to Vendor",
};

const REASON_SEVERITY = {
  damage: "bg-red-100 text-red-700",
  wrong_purchase: "bg-orange-100 text-orange-700",
  recount: "bg-blue-100 text-blue-700",
  return_to_vendor: "bg-purple-100 text-purple-700",
};

export default function StockAdjustmentMain() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.AuthReducer);
  const { products, specificVarients } = useSelector((state) => state.ProductReducer);
  const { allVendors } = useSelector((state) => state.VendorReducer);

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getVendors());
  }, [dispatch]);

  // --- Form state ---
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVarient, setSelectedVarient] = useState(null);
  const [reason, setReason] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [quantityChange, setQuantityChange] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedProduct) {
      dispatch(getspecificVarientsByProduct(selectedProduct));
      setSelectedVarient(null);
    }
  }, [selectedProduct, dispatch]);

  const varientOptions = useMemo(() => {
    if (!Array.isArray(specificVarients)) return [];
    return specificVarients.map((v) => ({
      label: v.properties?.map((p) => `${p.name}: ${p.value}`).join(" | ") || "Default",
      value: v.varient_id,
    }));
  }, [specificVarients]);

  // Current stock is the same varients.quantity the customer-facing site
  // reads — most products' stock is set directly on the product/variant
  // form, never through a purchase order, so it must not be recomputed
  // from purchases-minus-sales (that gives 0 for anything never run
  // through the Purchase workflow). Fetched fresh whenever the variant
  // changes so it reflects the latest value.
  const [currentStock, setCurrentStock] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);

  useEffect(() => {
    if (!selectedVarient) {
      setCurrentStock(null);
      return;
    }
    let cancelled = false;
    setLoadingStock(true);
    backendVerifiedUser
      .get("/get-current-stock", { params: { varient_id: selectedVarient } })
      .then((res) => {
        if (!cancelled) setCurrentStock(res.data.currentStock);
      })
      .catch(() => {
        if (!cancelled) setCurrentStock(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingStock(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedVarient]);

  const projectedStock = currentStock != null && quantityChange ? currentStock + quantityChange : null;

  const handleSubmit = async () => {
    if (!selectedProduct) return toast.error("Please select a product.");
    if (!selectedVarient) return toast.error("Please select a variant.");
    if (!reason) return toast.error("Please select a reason.");
    if (reason === "return_to_vendor" && !vendorId) return toast.error("Please select a vendor for a return.");
    if (!quantityChange) return toast.error("Please enter a non-zero quantity change.");
    if (projectedStock != null && projectedStock < 0) {
      return toast.error(`This would take stock negative. Current stock: ${currentStock}`);
    }

    setSubmitting(true);
    try {
      const response = await backendVerifiedUser.post("/add-stock-adjustment", {
        product_id: selectedProduct,
        varient_id: selectedVarient,
        vendor_id: reason === "return_to_vendor" ? vendorId : null,
        reason,
        quantity_change: quantityChange,
        notes,
        userId: user?.id,
      });
      toast.success(response.data?.success?.msg || "Stock adjustment recorded successfully!");
      setSelectedProduct(null);
      setSelectedVarient(null);
      setReason(null);
      setVendorId(null);
      setQuantityChange(null);
      setNotes("");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || "Failed to record stock adjustment.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- History / report state ---
  const today = useMemo(() => new Date(), []);
  const [rangeMode, setRangeMode] = useState("month");
  const [singleDate, setSingleDate] = useState(today);
  const [dateRange, setDateRange] = useState([
    new Date(today.getFullYear(), today.getMonth(), 1),
    today,
  ]);
  const [reasonFilter, setReasonFilter] = useState("");
  const [report, setReport] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const { startDate, endDate } = useMemo(
    () => computeDateRange(rangeMode, today, singleDate, dateRange),
    [rangeMode, singleDate, dateRange, today]
  );

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (reasonFilter) params.reason = reasonFilter;

      const response = await backendVerifiedUser.get("/get-stock-adjustments", { params });
      setReport(response.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, reasonFilter]);

  const summary = report?.summary || {};
  const adjustments = report?.adjustments || [];

  const kpiData = [
    { amount: summary.totalAdjustments || 0, title: "Total Adjustments", icon: <TbAdjustmentsAlt /> },
    { amount: summary.totalIncreased || 0, title: "Units Increased", icon: <TbTrendingUp /> },
    { amount: summary.totalDecreased || 0, title: "Units Decreased", icon: <TbTrendingDown /> },
    { amount: summary.damageCount || 0, title: "Damage Adjustments", icon: <MdOutlineReportProblem /> },
  ];

  const handleExport = () => {
    if (!report) return;

    const rows = [
      ["Date", "Product", "Article #", "Reason", "Vendor", "Qty Change", "Previous Qty", "New Qty", "Notes", "Adjusted By"],
      ...adjustments.map((a) => [
        new Date(a.created_at).toLocaleString(),
        a.product_title,
        a.article_name,
        REASON_LABELS[a.reason] || a.reason,
        a.vendor_name || "",
        a.quantity_change,
        a.previous_quantity,
        a.new_quantity,
        a.notes || "",
        a.created_by_name || "",
      ]),
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Stock Adjustments");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `stock-adjustments-${startDate || "all"}-to-${endDate || "all"}.xlsx`);
  };

  const reasonBody = (rowData) => (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${REASON_SEVERITY[rowData.reason] || "bg-gray-100 text-gray-700"}`}>
      {REASON_LABELS[rowData.reason] || rowData.reason}
    </span>
  );

  const quantityChangeBody = (rowData) => (
    <span className={`font-semibold ${rowData.quantity_change > 0 ? "text-green-600" : "text-red-600"}`}>
      {rowData.quantity_change > 0 ? `+${rowData.quantity_change}` : rowData.quantity_change}
    </span>
  );

  const dateBody = (rowData) => new Date(rowData.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

  return (
    <div className="px-4">
      <div className="font-semibold text-black/50 text-xl ml-1 my-3">Stock Adjustments</div>

      {/* New Adjustment Form */}
      <div className="card shadow-md p-4 mb-6">
        <div className="text-lg font-semibold text-gray-700 mb-4">New Adjustment</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-500">Product</label>
            <Dropdown
              value={selectedProduct}
              options={products?.filter((p) => p.is_deleted !== 1)}
              optionLabel={(o) => `${o.title} - ${o.article_name}`}
              optionValue="id"
              filter
              filterBy="title,article_name"
              placeholder="Search by title or article number"
              onChange={(e) => setSelectedProduct(e.value)}
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-500">Variant</label>
            <Dropdown
              value={selectedVarient}
              options={varientOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="Select variant"
              onChange={(e) => setSelectedVarient(e.value)}
              disabled={!selectedProduct}
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-500">Current Stock</label>
            <div className="h-[42px] flex items-center px-3 rounded border border-gray-200 bg-gray-50 font-semibold text-gray-700">
              {loadingStock ? "Loading..." : currentStock != null ? currentStock : "—"}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-500">Reason</label>
            <Dropdown
              value={reason}
              options={REASON_OPTIONS}
              optionLabel="name"
              optionValue="value"
              placeholder="Select reason"
              onChange={(e) => setReason(e.value)}
              className="w-full"
            />
          </div>

          {reason === "return_to_vendor" && (
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-500">Vendor</label>
              <Dropdown
                value={vendorId}
                options={allVendors}
                optionLabel="name"
                optionValue="id"
                filter
                placeholder="Select vendor"
                onChange={(e) => setVendorId(e.value)}
                className="w-full"
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm text-gray-500">Quantity Change (+ / -)</label>
            <InputNumber
              value={quantityChange}
              onValueChange={(e) => setQuantityChange(e.value)}
              placeholder="e.g. -5 or 10"
              className="w-full"
              showButtons={false}
            />
          </div>

          {projectedStock != null && (
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-500">Stock After Adjustment</label>
              <div className={`h-[42px] flex items-center px-3 rounded border font-semibold ${projectedStock < 0 ? "border-red-300 bg-red-50 text-red-600" : "border-green-200 bg-green-50 text-green-700"}`}>
                {projectedStock}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1 lg:col-span-2">
            <label className="text-sm text-gray-500">Notes</label>
            <InputTextarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Invoice #1234 had wrong quantity, correcting here"
              rows={1}
              autoResize
              className="w-full"
            />
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            label="Save Adjustment"
            icon="pi pi-check"
            className="p-primary-btn"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitting}
          />
        </div>
      </div>

      {/* History / Report */}
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-black/50 text-lg ml-1">History &amp; Report</div>
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          className="p-primary-btn"
          onClick={handleExport}
          disabled={!report}
        />
      </div>

      <ReportsDateFilter
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
        singleDate={singleDate}
        setSingleDate={setSingleDate}
        dateRange={dateRange}
        setDateRange={setDateRange}
        loading={loadingHistory}
      >
        <div className="flex flex-col gap-1">
          <label className="text-sm text-gray-500">Reason</label>
          <Dropdown
            value={reasonFilter}
            options={[{ name: "All Reasons", value: "" }, ...REASON_OPTIONS]}
            optionLabel="name"
            optionValue="value"
            onChange={(e) => setReasonFilter(e.value)}
            className="w-56"
          />
        </div>
      </ReportsDateFilter>

      {report && (
        <>
          <DashboardTiles data={kpiData} />

          <div className="mt-6 card shadow-md">
            <DataTable
              value={adjustments}
              stripedRows
              size="small"
              paginator
              rows={50}
              rowsPerPageOptions={[25, 50, 100]}
              emptyMessage="No stock adjustments for the selected filters."
            >
              <Column field="created_at" header="Date" body={dateBody} sortable />
              <Column field="product_title" header="Product" sortable />
              <Column field="article_name" header="Article #" style={{ width: "110px" }} />
              <Column field="reason" header="Reason" body={reasonBody} sortable />
              <Column field="vendor_name" header="Vendor" body={(r) => r.vendor_name || "N/A"} />
              <Column field="quantity_change" header="Qty Change" body={quantityChangeBody} sortable />
              <Column field="previous_quantity" header="Before" />
              <Column field="new_quantity" header="After" />
              <Column field="notes" header="Notes" body={(r) => r.notes || "—"} />
              <Column field="created_by_name" header="Adjusted By" body={(r) => r.created_by_name || "N/A"} />
            </DataTable>
          </div>
        </>
      )}
    </div>
  );
}
